import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import api from '../services/api';
import DateField from './DateField';
import SterilizationEntries, {
  SterilizationEntry, emptyEntry, validateSterilizations, toPayload,
} from './SterilizationEntries';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface Props {
  open: boolean;
  onClose: () => void;
  municipalities: string[];
  /** null = add, an existing record = edit (only its id is relied upon). */
  expense: any | null;
  /** Called after a successful save, with a ready-to-show recap message. */
  onSaved: (message: string) => void;
}

/**
 * Add or edit a generic / multi-cat invoice, including the sterilizations it
 * carries. One component for both pages and both modes: when the edit dialog was
 * a separate copy it silently lost fields the add dialog had.
 */
const MunicipalityExpenseDialog: React.FC<Props> = ({
  open, onClose, municipalities, expense, onSaved,
}) => {
  const { t } = useTranslation();
  const editing = !!expense;

  const [form, setForm] = useState<any>({});
  const [file, setFile] = useState<File | null>(null);
  const [existingFile, setExistingFile] = useState<string | null>(null);
  const [sterOn, setSterOn] = useState(false);
  const [sterEntries, setSterEntries] = useState<SterilizationEntry[]>([emptyEntry()]);
  const [sterDate, setSterDate] = useState('');
  const [sterWaived, setSterWaived] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setForm({ expense_date: todayStr(), municipality: '' });
    setFile(null);
    setExistingFile(null);
    setSterOn(false);
    setSterEntries([emptyEntry()]);
    setSterDate(todayStr());
    setSterWaived(false);
    setError(null);
  }, []);

  // On open: a blank form for a new invoice, or the saved one — sterilizations
  // included — so editing shows exactly what is on record.
  useEffect(() => {
    if (!open) return;
    reset();
    if (!expense?.id) return;
    setLoading(true);
    api.get(`/api/municipality-expenses/${expense.id}`)
      .then(({ data }) => {
        setForm({
          municipality: data.municipality || '',
          expense_date: data.expense_date || '',
          invoice_number: data.invoice_number || '',
          description: data.description || '',
          amount: data.amount ?? '',
          notes: data.notes_manual || '',
        });
        setExistingFile(data.invoice_file_path || null);
        const list: SterilizationEntry[] = (data.sterilizations || []).map((s: any) => ({
          chip_id: s.chip_id || '',
          sex: s.sex || '',
          noChip: !s.chip_id,
        }));
        setSterOn(list.length > 0);
        setSterEntries(list.length > 0 ? list : [emptyEntry()]);
        setSterDate(data.sterilization_date || data.expense_date || '');
        setSterWaived(!!data.recovery_waived);
      })
      .catch((e: any) => setError(e?.response?.data?.detail || String(e?.message || 'Error')))
      .finally(() => setLoading(false));
  }, [open, expense, reset]);

  // Mirrors compose_invoice_notes on the backend, for a preview only: the stored
  // text is always the one the server builds from what was actually saved.
  const autoNoteLine = (() => {
    const chips = sterEntries.filter((e) => !e.noChip && e.chip_id.trim()).map((e) => e.chip_id.trim());
    const nochip = sterEntries.filter((e) => e.noChip).length;
    if (chips.length === 0 && nochip === 0) return '';
    const parts = [...chips];
    if (nochip > 0) parts.push(`${nochip} senza chip`);
    return `Sterilizzazione ${parts.join(', ')}`;
  })();

  const recap = (data: any): string => {
    if (!editing) {
      let msg = t('sterilizationBatch.savedSummary', {
        created: data.created_count, linked: data.linked_count, skipped: data.skipped_count,
      });
      const skipped = (data.results || []).filter((r: any) => r.outcome === 'skipped');
      if (skipped.length > 0) msg += ` ${skippedList(skipped)}`;
      return msg;
    }
    let msg = t('sterilizationBatch.updatedSummary', {
      created: data.created_count, updated: data.updated_count, removed: data.removed_count,
    });
    if (data.cats_deleted > 0) msg += ` ${t('sterilizationBatch.catsDeleted', { count: data.cats_deleted })}`;
    if (data.cats_kept > 0) msg += ` ${t('sterilizationBatch.catsKept', { count: data.cats_kept })}`;
    const skipped = (data.results || []).filter((r: any) => r.outcome === 'skipped');
    if (skipped.length > 0) msg += ` ${skippedList(skipped)}`;
    return msg;
  };

  const skippedList = (skipped: any[]): string => {
    const list = skipped.map((r: any) => {
      const who = r.chip_id || t('sterilizationBatch.noChipLabel');
      const why = r.reason === 'already_sterilized'
        ? t('sterilizationBatch.reasonAlreadySterilized')
        : t('sterilizationBatch.reasonDuplicate');
      return `${who} (${why})`;
    }).join(', ');
    return t('sterilizationBatch.skippedDetail', { list });
  };

  const handleSave = async () => {
    setError(null);
    if (!form.municipality) { setError(t('expense.muniRequired')); return; }
    if (!form.expense_date) { setError(t('expense.dateRequired')); return; }
    if (sterOn) {
      const problem = validateSterilizations(sterEntries);
      if (problem) { setError(t(problem)); return; }
    }
    setSaving(true);
    try {
      let filePath = existingFile;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await api.post('/api/upload/invoice', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        filePath = up.data.file_path;
      }
      const body = {
        municipality: form.municipality,
        expense_date: form.expense_date,
        description: form.description || null,
        amount: Number(form.amount || 0),
        invoice_number: form.invoice_number || null,
        notes: form.notes || null,
        invoice_file_path: filePath,
        sterilization_date: sterOn ? (sterDate || form.expense_date) : null,
        recovery_waived: sterOn ? sterWaived : false,
        sterilizations: sterOn ? toPayload(sterEntries) : [],
      };
      const res = editing
        ? await api.put(`/api/municipality-expenses/${expense.id}`, body)
        : await api.post('/api/municipality-expenses', body);
      onClose();
      onSaved(sterOn || res.data.removed_count > 0 ? recap(res.data) : t('expense.muniSaved'));
    } catch (e: any) {
      setError(e?.response?.data?.detail || String(e?.message || 'Error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? t('expense.editMunicipal') : t('external.addCumulative')}</DialogTitle>
      <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Alert severity="warning">{t('external.cumulativeInfo')}</Alert>
            <Alert severity="info">{t('external.cumulativePreferSingle')}</Alert>

            <FormControl fullWidth required>
              <InputLabel>{t('cat.municipality')}</InputLabel>
              <Select
                label={t('cat.municipality')}
                value={form.municipality || ''}
                onChange={(e) => setForm({ ...form, municipality: e.target.value })}
              >
                {municipalities.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>

            <DateField
              label={t('expense.date')}
              value={form.expense_date || ''}
              onChange={(v: string) => setForm({ ...form, expense_date: v })}
            />
            <TextField
              label={t('expense.invoiceNumber')}
              value={form.invoice_number || ''}
              onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
            />
            <TextField
              label={t('expense.description')}
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
            />
            <TextField
              label={t('expense.amount')}
              type="text"
              inputMode="decimal"
              value={form.amount ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setForm({ ...form, amount: v });
              }}
            />

            <SterilizationEntries
              enabled={sterOn}
              onEnabledChange={setSterOn}
              entries={sterEntries}
              onEntriesChange={setSterEntries}
              procedureDate={sterDate || form.expense_date || ''}
              onProcedureDateChange={setSterDate}
              recoveryWaived={sterWaived}
              onRecoveryWaivedChange={setSterWaived}
            />

            {sterOn && autoNoteLine && (
              <Box sx={{ p: 1.5, bgcolor: '#f1f3ff', borderRadius: 2, border: '1px solid #d8ddff' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('sterilizationBatch.autoNotePreview')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                  {autoNoteLine}
                </Typography>
              </Box>
            )}

            <TextField
              label={t('cat.notes')}
              value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              multiline
              rows={3}
              placeholder={t('external.notesChipPlaceholder')}
              helperText={t('external.notesChipHint')}
            />

            <Box sx={{ p: 1.5, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#f9f9f9' }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {t('medical.invoiceUploadOptional')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button variant="outlined" component="label" startIcon={<AttachFileIcon />} size="small" sx={{ textTransform: 'none' }}>
                  {t('common.browseFiles')}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => { if (e.target.files && e.target.files[0]) setFile(e.target.files[0]); }}
                  />
                </Button>
                {file && (
                  <Button variant="outlined" color="error" size="small" onClick={() => setFile(null)} sx={{ textTransform: 'none' }}>
                    {t('common.clear')}
                  </Button>
                )}
              </Box>
              {(file || existingFile) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {file ? `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)` : t('common.fileSelected')}
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
          {saving ? <CircularProgress size={20} /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MunicipalityExpenseDialog;
